import { useState, useEffect, useCallback } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { api } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type ChangeStatus = 'pending' | 'approved' | 'documented' | 'completed' | 'overdue' | 'rejected' | 'failed';
type RequestType = 'reserve_change' | 'status_change' | 'data_update' | 'party_update';
type FilterTab = 'pending' | 'overdue' | 'approved' | 'rejected' | 'documented' | 'completed' | 'all';

interface ChangeEntry {
  field: string;
  field_label: string;
  old_value: string;
  new_value: string;
  display_format?: string;
}

interface ChangeRequest {
  id: string;
  claim_id: string;
  claim_number: string;
  request_type: RequestType;
  changes: ChangeEntry[];
  reason: string;
  priority: 'normal' | 'urgent';
  status: ChangeStatus;
  handler_name: string;
  submitted_at: string;
  reviewed_at: string | null;
  reviewer_name: string | null;
  reviewer_note: string | null;
  documented_at: string | null;
  verified_at: string | null;
  verified_by: string | null;
  fh_diary_id: string | null;
  fh_note_id: string | null;
  stubbed: boolean;
  error_message: string | null;
}

interface ChangeStats {
  pending: number;
  approved: number;
  rejected: number;
  documented: number;
  completed: number;
  overdue: number;
  failed: number;
  total: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatValue(entry: ChangeEntry): string {
  if (entry.display_format === 'currency') {
    const num = parseFloat(entry.new_value);
    if (!isNaN(num)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(num);
    }
  }
  return entry.new_value || '—';
}

function formatOldValue(entry: ChangeEntry): string {
  if (entry.display_format === 'currency') {
    const num = parseFloat(entry.old_value);
    if (!isNaN(num)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(num);
    }
  }
  return entry.old_value || '—';
}

function formatDelta(entry: ChangeEntry): string | null {
  if (entry.display_format !== 'currency') return null;
  const oldNum = parseFloat(entry.old_value);
  const newNum = parseFloat(entry.new_value);
  if (isNaN(oldNum) || isNaN(newNum)) return null;
  const delta = newNum - oldNum;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Math.abs(delta));
  return delta >= 0 ? `+${formatted}` : `-${formatted}`;
}

function changeSummary(request: ChangeRequest): string {
  return request.changes
    .map((c) => `${c.field_label}: ${formatOldValue(c)} → ${formatValue(c)}`)
    .join(', ');
}

function timeAgo(dateStr: string | null | undefined): { text: string; severity: 'normal' | 'overdue' | 'critical' } | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const hours = (Date.now() - d.getTime()) / (1000 * 60 * 60);
  if (hours < 1) return { text: 'less than an hour ago', severity: 'normal' };
  if (hours < 24) return { text: `${Math.floor(hours)} hours ago`, severity: 'normal' };
  const days = Math.floor(hours / 24);
  if (days === 1) return { text: '1 day ago', severity: hours > 24 ? 'overdue' : 'normal' };
  return {
    text: `${days} days ago`,
    severity: hours > 72 ? 'critical' : 'overdue',
  };
}

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  reserve_change: 'Reserve Change',
  status_change: 'Status Change',
  data_update: 'Data Update',
  party_update: 'Party Update',
};

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', className: 'bg-blue-100 text-blue-800' },
  documented: { label: 'Documented', className: 'bg-green-100 text-green-800' },
  completed: { label: 'Verified', className: 'bg-emerald-100 text-emerald-800' },
  overdue: { label: 'Overdue', className: 'bg-red-100 text-red-800' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-800' },
};

function StatusBadge({ status, stubbed }: { status: ChangeStatus; stubbed?: boolean }) {
  if (status === 'documented' && stubbed) {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800">
        Pending FH API
      </span>
    );
  }
  const config = STATUS_CONFIG[status] ?? { label: status, className: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

function TypeBadge({ type }: { type: RequestType }) {
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
      {REQUEST_TYPE_LABELS[type] ?? type}
    </span>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────

function StatsBar({ stats }: { stats: ChangeStats | null }) {
  const items = [
    { label: 'Pending', value: stats?.pending ?? 0, className: 'text-amber-700' },
    { label: 'Overdue', value: stats?.overdue ?? 0, className: 'text-red-600' },
    { label: 'Documented', value: stats?.documented ?? 0, className: 'text-green-700' },
    { label: 'Verified', value: stats?.completed ?? 0, className: 'text-emerald-700' },
    { label: 'Rejected', value: stats?.rejected ?? 0, className: 'text-red-600' },
    { label: 'Total', value: stats?.total ?? 0, className: 'text-gray-700' },
  ];

  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
      {items.map((item) => (
        <div key={item.label} className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500">{item.label}</p>
          <p className={`text-2xl font-bold tabular-nums mt-0.5 ${item.className}`}>
            {stats === null ? '—' : item.value}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Authorization Modal ──────────────────────────────────────────────────────

interface AuthorizationModalProps {
  request: ChangeRequest;
  approverName: string;
  onConfirm: (note: string) => void;
  onCancel: () => void;
  loading: boolean;
}

function AuthorizationModal({ request, approverName, onConfirm, onCancel, loading }: AuthorizationModalProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState('');
  const isFinancial = request.request_type === 'reserve_change';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6 space-y-5">
        {/* Header */}
        <div>
          <h3 className="text-lg font-bold text-gray-900">Authorize Change Request</h3>
          {isFinancial && (
            <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 inline-block">
              Financial Change — Reserve Adjustment
            </p>
          )}
        </div>

        {/* Authorization identity */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{approverName}</p>
              <p className="text-xs text-gray-500">{new Date().toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </div>
        </div>

        {/* Change details */}
        <div>
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">Change Details</p>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            <div className="px-3 py-2 text-xs">
              <span className="text-gray-500">Claim:</span>{' '}
              <span className="font-mono font-medium text-blue-700">{request.claim_number || request.claim_id}</span>
            </div>
            {request.changes.map((entry, i) => {
              const delta = formatDelta(entry);
              return (
                <div key={i} className="px-3 py-2">
                  <p className="text-xs font-medium text-gray-700">{entry.field_label}</p>
                  <div className="flex items-baseline gap-2 mt-0.5 text-sm">
                    <span className="text-gray-400 line-through text-xs">{formatOldValue(entry)}</span>
                    <span className="text-gray-400">→</span>
                    <span className="font-semibold text-gray-900">{formatValue(entry)}</span>
                    {delta && (
                      <span className={`text-xs font-medium ${delta.startsWith('+') ? 'text-red-600' : 'text-green-700'}`}>
                        ({delta})
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Optional note */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Add a note for the handler..."
            disabled={loading}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-60"
          />
        </div>

        {/* Confirmation checkbox */}
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            disabled={loading}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            I, <span className="font-semibold">{approverName}</span>, authorize {isFinancial ? 'this financial change' : 'this change'} to be applied.
          </span>
        </label>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(note.trim())}
            disabled={!confirmed || loading}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Authorizing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Authorize
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

interface DetailPanelProps {
  request: ChangeRequest;
  approverName: string;
  onReview: (id: string, action: 'approve' | 'reject', note: string) => Promise<void>;
  onRetry: (id: string) => Promise<void>;
}

function DetailPanel({ request, approverName, onReview, onRetry }: DetailPanelProps) {
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState<'approve' | 'reject' | 'retry' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  async function handleApproveConfirm(note: string) {
    setActionLoading('approve');
    setActionError(null);
    try {
      await onReview(request.id, 'approve', note);
      setShowAuthModal(false);
    } catch (err: any) {
      setActionError(err.message || 'Action failed.');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReject() {
    if (!rejectNote.trim()) {
      setActionError('A note is required when rejecting.');
      return;
    }
    setActionLoading('reject');
    setActionError(null);
    try {
      await onReview(request.id, 'reject', rejectNote.trim());
    } catch (err: any) {
      setActionError(err.message || 'Action failed.');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRetry() {
    setActionLoading('retry');
    setActionError(null);
    try {
      await onRetry(request.id);
    } catch (err: any) {
      setActionError(err.message || 'Retry failed.');
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div className="bg-gray-50 border-t border-gray-100 px-4 py-4 space-y-4">

      {/* Changes */}
      <div>
        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">Changes</p>
        <div className="space-y-2">
          {request.changes.map((entry, i) => {
            const delta = formatDelta(entry);
            return (
              <div key={i} className="flex flex-wrap items-baseline gap-2 text-sm">
                <span className="font-medium text-gray-700 min-w-[140px]">{entry.field_label}</span>
                <span className="text-gray-400 line-through text-xs">{formatOldValue(entry)}</span>
                <span className="text-gray-400">→</span>
                <span className="font-semibold text-gray-900">{formatValue(entry)}</span>
                {delta && (
                  <span className={`text-xs font-medium ${delta.startsWith('+') ? 'text-red-600' : 'text-green-700'}`}>
                    ({delta})
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Reason */}
      <div>
        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Handler Reason</p>
        <p className="text-sm text-gray-700">{request.reason}</p>
      </div>

      {/* Timestamps */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
        <span>Submitted: {formatDateTime(request.submitted_at)}</span>
        {request.reviewed_at && <span>Reviewed: {formatDateTime(request.reviewed_at)}</span>}
        {request.documented_at && <span>Documented: {formatDateTime(request.documented_at)}</span>}
        {request.verified_at && <span className="text-emerald-600">Verified: {formatDateTime(request.verified_at)}</span>}
      </div>

      {/* Verified badge */}
      {request.status === 'completed' && (
        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          <svg className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-emerald-700">
            Change verified in FileHandler report database{request.verified_by === 'system-auto' ? ' (auto-detected)' : ''}.
          </p>
        </div>
      )}

      {/* Overdue warning */}
      {request.status === 'overdue' && (() => {
        const info = timeAgo(request.documented_at || request.reviewed_at);
        return (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className={`text-xs ${info?.severity === 'critical' ? 'text-red-800 font-bold' : 'text-red-700'}`}>
              {info?.severity === 'critical' ? 'Critically overdue' : 'Overdue'} — approved {info?.text || ''}.
              Change has not been applied in FileHandler.
            </p>
          </div>
        );
      })()}

      {/* FH API tracking */}
      {(request.status === 'documented' || request.status === 'completed' || request.status === 'overdue') && (
        <div className="text-xs text-gray-500 space-y-0.5">
          {request.stubbed ? (
            <p className="text-amber-600 font-medium">
              FileHandler diary/note will be created when API access is enabled.
            </p>
          ) : (
            <>
              {request.fh_diary_id && <p>Diary ID: <span className="font-mono">{request.fh_diary_id}</span></p>}
              {request.fh_note_id && <p>Note ID: <span className="font-mono">{request.fh_note_id}</span></p>}
            </>
          )}
        </div>
      )}

      {/* Error message */}
      {request.error_message && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <p className="text-xs text-red-700">{request.error_message}</p>
        </div>
      )}

      {/* Authorization info (non-pending) */}
      {request.status !== 'pending' && request.reviewer_name && (
        <div className="flex items-start gap-2.5 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
          <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <p className="text-sm text-gray-800">
              {request.status === 'rejected' ? 'Rejected' : 'Authorized'} by{' '}
              <span className="font-bold text-gray-900">{request.reviewer_name}</span>
              {request.reviewed_at && (
                <span className="text-gray-500"> on {formatDateTime(request.reviewed_at)}</span>
              )}
            </p>
            {request.reviewer_note && (
              <p className="text-sm text-gray-600 mt-0.5 italic">"{request.reviewer_note}"</p>
            )}
          </div>
        </div>
      )}

      {/* ── Pending actions ─────────────────────────────────────────── */}
      {request.status === 'pending' && (
        <div className="space-y-3 pt-1 border-t border-gray-200">
          {/* Authorize + Reject buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowAuthModal(true)}
              disabled={actionLoading !== null}
              className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Authorize
            </button>
          </div>

          {/* Reject section */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={2}
              placeholder="Required: explain why this is being rejected..."
              disabled={actionLoading !== null}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none disabled:opacity-60"
            />
            <button
              onClick={handleReject}
              disabled={actionLoading !== null}
              className="mt-1.5 inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-red-50 disabled:opacity-50 text-red-600 border border-red-300 text-sm font-medium rounded-lg transition-colors"
            >
              {actionLoading === 'reject' ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Rejecting...
                </>
              ) : 'Reject'}
            </button>
          </div>
        </div>
      )}

      {/* Authorization modal */}
      {showAuthModal && (
        <AuthorizationModal
          request={request}
          approverName={approverName}
          onConfirm={handleApproveConfirm}
          onCancel={() => setShowAuthModal(false)}
          loading={actionLoading === 'approve'}
        />
      )}

      {/* ── Failed — retry ──────────────────────────────────────────── */}
      {request.status === 'failed' && (
        <div className="pt-1 border-t border-gray-200">
          <button
            onClick={handleRetry}
            disabled={actionLoading !== null}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-700 border border-gray-300 text-sm font-medium rounded-lg transition-colors"
          >
            {actionLoading === 'retry' ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Retrying...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry Apply
              </>
            )}
          </button>
        </div>
      )}

      {/* Action error */}
      {actionError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const FILTER_TABS: { id: FilterTab; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'documented', label: 'Documented' },
  { id: 'completed', label: 'Verified' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];

export default function SupervisorDashboard() {
  const [activeTab, setActiveTab] = useState<FilterTab>('pending');
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [stats, setStats] = useState<ChangeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortDesc, setSortDesc] = useState(true);
  const [approverName, setApproverName] = useState('Supervisor');

  // Batch selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchModal, setBatchModal] = useState<{ action: 'approve' | 'reject'; ids: string[] } | null>(null);
  const [batchNote, setBatchNote] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);

  // Fetch approver name from Cognito
  useEffect(() => {
    fetchUserAttributes()
      .then((attrs) => {
        if (attrs.name) setApproverName(attrs.name);
        else if (attrs.email) setApproverName(attrs.email);
      })
      .catch(() => {
        // Fallback already set to 'Supervisor'
      });
  }, []);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (activeTab !== 'all') params.status = activeTab;

      const [changesRes, statsRes] = await Promise.allSettled([
        (api as any).getChanges(params),
        (api as any).getChangeStats(),
      ]);

      if (changesRes.status === 'fulfilled') {
        const raw = changesRes.value;
        const items: ChangeRequest[] = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.items)
          ? raw.items
          : [];
        setRequests(items);
      } else {
        setError((changesRes as PromiseRejectedResult).reason?.message || 'Failed to load change requests.');
      }

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Initial load + tab change
  useEffect(() => {
    setLoading(true);
    setExpandedId(null);
    setSelectedIds(new Set());
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 30_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // ── Review handler ────────────────────────────────────────────────────────────

  async function handleReview(id: string, action: 'approve' | 'reject', note: string) {
    const result = await (api as any).reviewChange(id, { action, note: note || undefined });
    // Update with server response (may be documented or approved)
    const updatedStatus = result?.status || (action === 'approve' ? 'documented' : 'rejected');
    setRequests((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              status: updatedStatus,
              reviewer_note: note || null,
              stubbed: result?.stubbed ?? false,
              fh_diary_id: result?.fh_diary_id ?? null,
              fh_note_id: result?.fh_note_id ?? null,
              documented_at: result?.documented_at ?? null,
            }
          : r
      )
    );
    // Refresh stats silently
    try {
      const fresh = await (api as any).getChangeStats();
      setStats(fresh);
    } catch {
      // ignore
    }
  }

  // ── Retry handler ─────────────────────────────────────────────────────────────

  async function handleRetry(id: string) {
    const result = await (api as any).retryChange(id);
    const updatedStatus = result?.status || 'documented';
    setRequests((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              status: updatedStatus,
              stubbed: result?.stubbed ?? false,
              fh_diary_id: result?.fh_diary_id ?? null,
              fh_note_id: result?.fh_note_id ?? null,
            }
          : r
      )
    );
  }

  // ── Batch handlers ──────────────────────────────────────────────────────────

  const pendingInView = requests.filter((r) => r.status === 'pending');
  const allPendingSelected = pendingInView.length > 0 && pendingInView.every((r) => selectedIds.has(r.id));

  // Financial change tracking for batch restrictions
  const selectedRequests = requests.filter((r) => selectedIds.has(r.id));
  const selectedFinancial = selectedRequests.filter((r) => r.request_type === 'reserve_change');
  const selectedNonFinancial = selectedRequests.filter((r) => r.request_type !== 'reserve_change');
  const hasFinancialSelected = selectedFinancial.length > 0;

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allPendingSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingInView.map((r) => r.id)));
    }
  }

  async function executeBatch() {
    if (!batchModal) return;
    setBatchLoading(true);
    setBatchError(null);
    try {
      // Defensive: filter out financial items from batch approve
      let ids = batchModal.ids;
      if (batchModal.action === 'approve') {
        ids = ids.filter((id) => {
          const req = requests.find((r) => r.id === id);
          return req?.request_type !== 'reserve_change';
        });
        if (ids.length === 0) {
          setBatchError('No non-financial items to approve. Financial changes require individual authorization.');
          setBatchLoading(false);
          return;
        }
      }
      const result = await (api as any).batchReview({
        request_ids: ids,
        action: batchModal.action,
        note: batchNote.trim() || undefined,
      });
      // Update local state
      const resultMap = new Map<string, any>();
      for (const r of result.results || []) {
        resultMap.set(r.request_id, r);
      }
      setRequests((prev) =>
        prev.map((req) => {
          const batchResult = resultMap.get(req.id);
          if (!batchResult || !batchResult.success) return req;
          return { ...req, status: batchResult.status || (batchModal.action === 'approve' ? 'documented' : 'rejected'), stubbed: batchResult.stubbed ?? false };
        })
      );
      setSelectedIds(new Set());
      setBatchModal(null);
      setBatchNote('');
      // Refresh stats
      try {
        const fresh = await (api as any).getChangeStats();
        setStats(fresh);
      } catch { /* ignore */ }
    } catch (err: any) {
      setBatchError(err.message || 'Batch operation failed.');
    } finally {
      setBatchLoading(false);
    }
  }

  // ── Sorted requests ───────────────────────────────────────────────────────────

  const sorted = [...requests].sort((a, b) => {
    const ta = new Date(a.submitted_at).getTime();
    const tb = new Date(b.submitted_at).getTime();
    return sortDesc ? tb - ta : ta - tb;
  });

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* Page header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Change Request Review</h1>
            <p className="text-sm text-gray-500 mt-0.5">Supervisor dashboard — review and approve handler change requests</p>
          </div>
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Stats bar */}
        <StatsBar stats={stats} />

        {/* Filter tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-1 overflow-x-auto">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setExpandedId(null);
                }}
                className={`shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.id !== 'all' && stats && stats[tab.id as keyof ChangeStats] > 0 && (
                  <span className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                    tab.id === 'pending' ? 'bg-amber-100 text-amber-700'
                    : tab.id === 'overdue' ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-600'
                  }`}>
                    {stats[tab.id as keyof ChangeStats]}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3">
            <svg className="animate-spin w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm text-gray-500">Loading change requests...</span>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 px-6 py-10 flex flex-col items-center gap-4">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-gray-700 font-medium">{error}</p>
            <button
              onClick={() => { setLoading(true); fetchData(); }}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        ) : sorted.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-16 text-center">
            <p className="text-gray-500 text-sm">No {activeTab !== 'all' ? activeTab : ''} change requests.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Batch action bar */}
            {selectedIds.size > 0 && (
              <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-blue-800">{selectedIds.size} selected</span>
                  {hasFinancialSelected ? (
                    <>
                      <button
                        onClick={() => {
                          // Auto-deselect financial items, keep non-financial
                          const nonFinIds = new Set(selectedNonFinancial.map((r) => r.id));
                          setSelectedIds(nonFinIds);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium rounded-lg transition-colors"
                      >
                        Deselect Financial ({selectedFinancial.length})
                      </button>
                      {selectedNonFinancial.length > 0 && (
                        <button
                          onClick={() => setBatchModal({ action: 'approve', ids: selectedNonFinancial.map((r) => r.id) })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          Approve Non-Financial ({selectedNonFinancial.length})
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => setBatchModal({ action: 'approve', ids: Array.from(selectedIds) })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Approve All
                    </button>
                  )}
                  <button
                    onClick={() => setBatchModal({ action: 'reject', ids: Array.from(selectedIds) })}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-red-50 text-red-600 border border-red-300 text-sm font-medium rounded-lg transition-colors"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Clear
                  </button>
                </div>
                {/* Financial change banner */}
                {hasFinancialSelected ? (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-xs font-medium text-red-700">
                      {selectedFinancial.length} financial change{selectedFinancial.length > 1 ? 's' : ''} selected — financial changes require individual authorization.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs text-amber-700">
                      Bulk authorization available for non-financial changes only.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {(activeTab === 'pending' || activeTab === 'all') && (
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={allPendingSelected && pendingInView.length > 0}
                          onChange={toggleSelectAll}
                          disabled={pendingInView.length === 0}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          title="Select all pending"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Claim #</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Handler</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Summary</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Priority</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Status</th>
                    <th
                      className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap cursor-pointer select-none hover:text-blue-600 transition-colors"
                      onClick={() => setSortDesc((prev) => !prev)}
                    >
                      Submitted
                      <span className="ml-1 text-gray-400">{sortDesc ? '↓' : '↑'}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((req) => {
                    const isExpanded = expandedId === req.id;
                    const isUrgent = req.priority === 'urgent';

                    const isOverdue = req.status === 'overdue';
                    const overdueInfo = isOverdue ? timeAgo(req.documented_at || req.reviewed_at) : null;

                    return (
                      <>
                        <tr
                          key={req.id}
                          onClick={() => setExpandedId(isExpanded ? null : req.id)}
                          className={[
                            'border-b border-gray-100 cursor-pointer transition-colors',
                            isExpanded ? 'bg-blue-50' : isOverdue ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-gray-50',
                            isOverdue ? 'border-l-4 border-l-red-500' : isUrgent ? 'border-l-4 border-l-orange-400' : '',
                          ].join(' ')}
                        >
                          {(activeTab === 'pending' || activeTab === 'all') && (
                            <td className="px-3 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                              {req.status === 'pending' ? (
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(req.id)}
                                  onChange={() => toggleSelect(req.id)}
                                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                              ) : <span className="w-4 h-4 block" />}
                            </td>
                          )}
                          <td className="px-4 py-3 font-mono text-blue-700 whitespace-nowrap font-medium">
                            {req.claim_number || req.claim_id}
                          </td>
                          <td className="px-4 py-3 text-gray-800 whitespace-nowrap">
                            {req.handler_name || '—'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <TypeBadge type={req.request_type} />
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                            {changeSummary(req)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {isUrgent ? (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700">
                                Urgent
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">Normal</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <StatusBadge status={req.status} stubbed={req.stubbed} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs">
                            <span className="text-gray-500">{formatDateTime(req.submitted_at)}</span>
                            {overdueInfo && (
                              <span className={`block text-[10px] font-medium mt-0.5 ${
                                overdueInfo.severity === 'critical' ? 'text-red-700 font-bold' : 'text-red-600'
                              }`}>
                                Overdue — approved {overdueInfo.text}
                              </span>
                            )}
                            {req.status === 'completed' && req.verified_at && (
                              <span className="block text-[10px] text-emerald-600 mt-0.5">
                                Verified {timeAgo(req.verified_at)?.text || ''}
                              </span>
                            )}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr key={`${req.id}-detail`}>
                            <td colSpan={(activeTab === 'pending' || activeTab === 'all') ? 8 : 7} className="p-0">
                              <DetailPanel
                                request={req}
                                approverName={approverName}
                                onReview={handleReview}
                                onRetry={handleRetry}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Auto-refresh notice */}
        <p className="text-xs text-gray-400 text-right">Auto-refreshes every 30 seconds.</p>
      </div>

      {/* Batch confirmation modal */}
      {batchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900">
              {batchModal.action === 'approve' ? 'Batch Approve' : 'Batch Reject'}
            </h3>
            {/* Defensive: warn if financial items somehow ended up in batch approve */}
            {batchModal.action === 'approve' && batchModal.ids.some((id) => requests.find((r) => r.id === id)?.request_type === 'reserve_change') && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-xs font-medium text-red-700">Financial changes detected — these require individual authorization and will be skipped.</p>
              </div>
            )}
            <p className="text-sm text-gray-600">
              {batchModal.action === 'approve'
                ? `Approve ${batchModal.ids.length} change request${batchModal.ids.length > 1 ? 's' : ''}?`
                : `Reject ${batchModal.ids.length} change request${batchModal.ids.length > 1 ? 's' : ''}?`}
            </p>

            {/* Summary list */}
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {batchModal.ids.map((id) => {
                const req = requests.find((r) => r.id === id);
                return req ? (
                  <div key={id} className="px-3 py-2 text-xs">
                    <span className="font-mono text-blue-700 font-medium">{req.claim_number || req.claim_id}</span>
                    <span className="text-gray-400 mx-2">—</span>
                    <span className="text-gray-600">{changeSummary(req)}</span>
                  </div>
                ) : null;
              })}
            </div>

            {/* Note */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Note {batchModal.action === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={batchNote}
                onChange={(e) => setBatchNote(e.target.value)}
                rows={2}
                placeholder={batchModal.action === 'approve'
                  ? 'Optional note (e.g., "Batch approved — routine reserve adjustments")'
                  : 'Required: reason for rejection'}
                disabled={batchLoading}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-60"
              />
            </div>

            {/* Error */}
            {batchError && (
              <p className="text-sm text-red-600">{batchError}</p>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-1">
              <button
                onClick={() => { setBatchModal(null); setBatchNote(''); setBatchError(null); }}
                disabled={batchLoading}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={executeBatch}
                disabled={batchLoading || (batchModal.action === 'reject' && !batchNote.trim())}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                  batchModal.action === 'approve'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {batchLoading && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {batchLoading
                  ? (batchModal.action === 'approve' ? 'Approving...' : 'Rejecting...')
                  : (batchModal.action === 'approve' ? `Approve ${batchModal.ids.length}` : `Reject ${batchModal.ids.length}`)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
